import snakeCaseKeys from 'snakecase-keys';
import { objectsToIDs } from 'shared/helpers/common';
import { getRootTransportBySlug, getRootTransport } from 'shared/helpers/transportTypes';
import {
  selectedActions,
  setFormVocabulary,
  carSetThunks,
  otherTransportSetThunks,
} from 'shared/actions/advertForm';
import queryString from 'query-string';
import { OTHER_ITEM, VOCABULARY } from 'shared/constants/advertForm';
import priceStatuses from 'shared/constants/json/priceStatuses.json';
import { getAllLocations } from 'shared/helpers/locale';
// import CITIES from 'shared/constants/json/cities.json';
import COUNTRIES from 'shared/constants/json/countries.json';
import { compareByTitle, objectToArray } from './arrays';
import API from '../constants/api';
import { PACKAGES_SORT } from 'shared/constants/payment';

export const getSelectedThunksBySlug = transportSlug => {
  switch (transportSlug) {
    case 'avto': {
      return carSetThunks;
    }
    default:
      return otherTransportSetThunks;
  }
};

export const detectTransportType = transportSlug => {
  const rootSlug = (getRootTransportBySlug(transportSlug) || {}).slug;

  return {
    isAvto: rootSlug === 'avto',
    isMoto: rootSlug === 'moto',
    isTruck: rootSlug === 'gruzovye',
    isBus: rootSlug === 'avtobus',
    isTechnics: rootSlug === 'spetstehnika',
    isTrailer: rootSlug === 'pritsepy',
    isWater: rootSlug === 'vodnyi-transport',
  };
};

export const postPhotos = (fetch, id, token) =>
  Promise.all(
    /* photos */ [].map(() =>
      fetch(API.ADVERT.photos(id), {
        method: 'POST',
        headers: { Authorization: `JWT ${token}` },
        body: '',
      }),
    ),
);

export const fetchVocabulary = (fetch, vocabularyState, transportType, parentTransportType) => {
  const missingVocabularyFields = Object.keys(vocabularyState).filter(key => !vocabularyState[key]);
  const vocabularyAPI = VOCABULARY[transportType.slug];

  return Promise.all(
    missingVocabularyFields.map(field =>
      vocabularyAPI[field]
        ? fetch(
            vocabularyAPI[field](
              parentTransportType.slug === 'moto' || field === 'categories'
                ? transportType.id
                : parentTransportType.id,
            ),
          )
            .then(r => (r.ok ? r.json() : []))
            .then(response => ({ [field]: response }))
        : Promise.resolve({}),
    ),
  ).then(data => data.reduce((result, pair) => ({ ...result, ...pair }), {}));
};

const fetchLocation = (fetch, id) =>
  id ? fetch(API.VOCABULARY.location(id)).then(r => r.json()) : Promise.resolve();

export const fillUserData = async (fetch, store, shouldFetchLocation = true) => {
  const state = store.getState();
  const email = state.user.user.email || null;
  const phones = (state.user.user.phones || [])
    .filter(phone => phone.is_verified)
    .map(phone => phone.number);

  store.dispatch(selectedActions.set.email(email));
  store.dispatch(selectedActions.set.phones(phones));
  if (state.user.user.name) {
    store.dispatch(selectedActions.set.contactName(state.user.user.name));
  }
  if (shouldFetchLocation) {
    const location = await (state.advertForm.selected.location ||
      fetchLocation(fetch, state.user.user.location));

    if (location) {
      store.dispatch(selectedActions.set.location(location));
    }
  }
};

export const fillVocabulary = async (fetch, store, transportType, parentTransport) => {
  const state = store.getState();
  const { vocabulary: vocabularyTmp } = state.advertForm;

  const { locations, ...vocabulary } = vocabularyTmp;

  // Do not fetch locations data from API
  const { makes, ...fetchedVocabulary } = await fetchVocabulary(
    fetch,
    vocabulary,
    transportType,
    parentTransport,
  );

  store.dispatch(
    setFormVocabulary({
      ...fetchedVocabulary,
      locations: getAllLocations(true),
      // locations: CITIES,
      // todo: refactor this logic. We need countries from may be driven cars, but not "europeanCountries".
      europeanCountries: COUNTRIES.filter(
        country => country.isEuropean || country.isUsa || country.includeInDrivenFrom,
      ),
      priceStatuses,
      makes: [
        ...(parentTransport.slug !== 'avto' ? [OTHER_ITEM] : []),
        ...(vocabulary.makes || makes.sort(compareByTitle)),
      ],
    }),
  );
};

export const normalizePlainOptionIDsToSelected = (options, optionsTree) => {
  const firstLevelOptions = optionsTree
    .map(category => category.options)
    .reduce((result, pack) => [...result, ...pack], []);

  const normalizedOptions = options.map(option => {
    const firstLevelParent = firstLevelOptions.find(
      firstLevelOption =>
        firstLevelOption.children && firstLevelOption.children.find(child => child.id === option),
    );

    return firstLevelParent ? { [firstLevelParent.id]: option } : { [option]: true };
  });

  return normalizedOptions.reduce((result, pack) => ({ ...result, ...pack }), {});
};

export const parseDecimal = (value, intLength, decimalLength) => {
  let newValue = value;

  if (value) {
    const strValue = `${value}`;
    const indexOfDot = strValue.indexOf('.');
    const dotPosition = indexOfDot >= 0 ? indexOfDot : strValue.length;
    const intPart = strValue.substring(0, Math.min(dotPosition, intLength));
    const decPart = strValue.substring(dotPosition + 1, dotPosition + 1 + decimalLength);

    newValue = `${intPart}${decPart ? `.${decPart}` : ''}`;
  }

  return newValue;
};

export const fetchAdvertAverage = (fetch, { make, model, year }) =>
  fetch(
    API.ADVERT.ALL(
      `?${queryString.stringify({
        make: make && make.id,
        model: model && model.id,
        year_from: year || undefined,
        year_to: year || undefined,
        per_page: 6,
      })}`,
    ),
  )
    .then(r => (r.ok ? r.json() : null))
    .then(adverts => adverts && adverts.results);

export const fetchAveragePrice = (
  fetch,
  { make, model, year, currency, mileage, modification = null },
) =>
  fetch(
    API.CMM.averagePrice({
      make: make && make.id,
      model: model && model.id,
      year,
      currency: currency && currency.id ? currency.id : undefined,
      modification: modification && modification.id ? modification.id : undefined,
      mileage: mileage * 1000,
    }),
  )
    .then(r => (r.ok ? r.json() : null))
    .then(averagePrice =>
      !averagePrice && modification && modification.id
        ? fetchAveragePrice(fetch, { make, model, year, currency, mileage: mileage * 1000 })
        : averagePrice,
    );

export const fetchAdvertItem = id =>
    fetch(API.ADVERT.ITEM(id))
    .then(r => r.json());

export const postAdvert = async (fetch, { photos = [], ...advert }, token) =>
  fetch(API.ADVERT.ALL(), {
    method: 'POST',
    headers: { Authorization: `JWT ${token}` },
    body: JSON.stringify({
      ...advert,
      photos,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Не удалось отправить форму. Попробуйте позже.');
      }

      return response;
    })
    .then(r => r.json());

export const updateAdvert = (fetch, token, id, payload, isSpare = false) =>
  fetch(isSpare ? API.SPARE_PARTS.ITEM(id) : API.ADVERT.ITEM(id), {
    method: 'PATCH',
    body: JSON.stringify(payload),
    headers: { Authorization: `JWT ${token}` },
  });

export const prepareAdverObject = (data, formData, user) => {
  const {
    options,
    advertFields,
    colorFeatures,
    transport,
    category,
    regNumber,
    drivenFrom,
    usedWarranty,
    ...formState,
  } = data;

  const rootTransport = getRootTransport(transport);
  const isMoto = rootTransport.slug === 'moto';

  const photos = formData.newExistingPhotos;
  if (!(formState.phones && formState.phones[0]) && user.phones) {
    formState.phones = user.phones.map(dat => dat.number);
  }
  
  return {
    ...snakeCaseKeys({
      ...objectsToIDs({
        ...formState,
        transport: isMoto ? transport : rootTransport,
        category: category || transport,
        reg_number: regNumber,
        driven_from: Number(drivenFrom),
        used_warranty: usedWarranty,
      }),
      ...advertFields,
      options: objectToArray(options).map(item => parseInt(item, 10)),
      colorFeatures: objectToArray(colorFeatures).map(item => parseInt(item, 10)),
    }),
    photos,
    'transport-group': rootTransport.slug,
    // [!!promoteInSocial && 'promote_in_social']: promoteInSocial,
  };
};

export const getSortedPakages = packages => {
  return packages.sort((a, b) => {
    if (!PACKAGES_SORT[a.slug] || !PACKAGES_SORT[b.slug]) return 0;
    return PACKAGES_SORT[a.slug] - PACKAGES_SORT[b.slug];
  });
};

/**
 * Is package should be disabled
 * @param {Object} user 
 * @param {Object} pkg 
 * @param {Object} advert 
 * @returns 
 */
export const isDisabledPackage = (user, pkg, advert) => {
  if (pkg.slug === user.tariff.slug) return true;
  if (pkg.slug === 'v2-maxi' && user.tariff.slug  === 'v2') return true;
  if (pkg.slug === 'v2-maxi' && advert.active === 1) return true;
  return false;
};



// WEBPACK FOOTER //
// ./src/shared/helpers/advertForm.js